import { KeyboardEvent, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import type { Suggestion, AddressData } from '@occ/api-client';
import Text from '@atoms/Text/Text';
import Input from '@atoms/Input/Input';
import ErrorMessage from '@molecules/Form/ErrorMessage';
import KEYS from '@helpers/keys';
import {
  SearchParamsWrapper,
  StyledListbox,
  StyledContainer,
  StyledInfoBox,
  StyledSpinner,
} from './FullAddressSearch.styles';
import useCart from '@hooks/useCart';
import FullAddressListItem from '@molecules/FullAddressListItem/FullAddressListItem';
import ScrollShadow from '@components/molecules/ScrollShadow/ScrollShadow';
import useFinishSubmission from '@molecules/FullAddressSearch/useFinishSubmission';
import { useAddressSearch } from './useAddressSearch';
import { useAddressValidation } from './useAddressValidation';
import useListboxNavigation from '@hooks/useListboxNavigation';

export type FullAddressProps = {
  afterSubmit?: () => void;
  // Optional to support tests passing a pre-fetched address
  customerAddress?: AddressData | null;
};

export const MIN_SEARCH_LENGTH = 3;

const FullAddressSearch = ({ afterSubmit }: FullAddressProps) => {
  const { cart } = useCart();
  const t = useTranslations('deliverypicker');
  const autoCompleteRef = useRef<HTMLDivElement>(null);
  // Custom message for input validation
  const [inputErrorMessage, setInputErrorMessage] = useState('');

  const [isValidatingAndSubmitting, setIsValidatingAndSubmitting] = useState(false);

  const finishSubmission = useFinishSubmission({ afterSubmit });

  const {
    savedCustomerAddress,
    suggestionForYou,
    inputValue,
    setInputValue,
    searchSuggestions,
    isLoading: isLoadingAddressSearch,
    onChange,
    onClear,
  } = useAddressSearch();

  const { validate } = useAddressValidation({
    savedCustomerAddress,
    setInputErrorMessage,
  });

  // Determine whether to use saved address or manual input. If validation
  // starts without any search result, the saved address has been clicked.
  const useSavedAddress = !!savedCustomerAddress && inputValue.length === 0;

  const isLoading = isLoadingAddressSearch || isValidatingAndSubmitting;

  const { getListItemProps } = useListboxNavigation({
    listRef: autoCompleteRef,
    onSelect: (index) => {
      const item = suggestions[index];
      if (item) {
        addressListItemOnClick(item);
      }
    },
  });

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case KEYS.TAB:
        e.currentTarget.blur();
        break;
      case KEYS.UP:
        e.preventDefault();
        break;
      case KEYS.DOWN:
        e.preventDefault();
        autoCompleteRef.current?.querySelector<HTMLElement>('[role="option"]')?.focus();
        break;
      default:
        break;
    }
  };

  // Starts the whole process of validating the address and submitting it
  const processAddressAndSubmit = async (addressValue: string) => {
    if (!addressValue) return;

    setIsValidatingAndSubmitting(true);
    try {
      const { addressParams, deliveryStoreId = '' } = await validate(addressValue, useSavedAddress);
      await finishSubmission(
        deliveryStoreId,
        addressParams,
        {
          longitude: 0,
          latitude: 0,
          firstName: cart?.deliveryAddress?.firstName || '',
          lastName: cart?.deliveryAddress?.lastName || '',
        },
        () => setIsValidatingAndSubmitting(false)
      );
    } catch {
      setIsValidatingAndSubmitting(false);
    }
  };

  // Setting input value, forcing input validation and submitting address
  const addressListItemOnClick = async (suggestion: Suggestion) => {
    const { street, street_number, letter, postalcode, locality } = suggestion;
    const formattedValue = `${street}${
      street_number || letter ? ' ' : ''
    }${street_number}${letter}, ${postalcode} ${locality}`;

    setInputErrorMessage('');
    setInputValue(formattedValue);
    await processAddressAndSubmit(formattedValue);
  };

  const getKeyFunction = (item: Suggestion, _index: number) => {
    return `${item.street}-${item.street_number}-${item.postalcode}${item.letter}-${item.locality}`;
  };

  const shouldShowSuggestionForYou = suggestionForYou?.street && inputValue.length === 0;
  const shouldShowSearchSuggestions = inputValue.length >= MIN_SEARCH_LENGTH;
  const suggestions = shouldShowSuggestionForYou ? [suggestionForYou] : (searchSuggestions ?? []);
  const showAutocomplete = !isLoading && (shouldShowSuggestionForYou || shouldShowSearchSuggestions);

  return (
    <>
      <SearchParamsWrapper>
        <Text type="body" size="medium" color="black">
          {t('info.homeDeliveryAddress')}
        </Text>

        <Input
          type="search"
          showSearchIcon
          aria-label="searchHomeAddress"
          name="searchHomeAddress"
          placeholder={t('placeholder.address')}
          value={inputValue}
          onChange={(e) => {
            setInputErrorMessage('');
            onChange(e.currentTarget.value);
          }}
          onClear={() => {
            setInputErrorMessage('');
            onClear();
          }}
          onKeyDown={handleKeyDown}
          clearable
          autoFocus
        />
        {inputValue && inputErrorMessage && <ErrorMessage name="searchHomeAddress">{inputErrorMessage}</ErrorMessage>}
      </SearchParamsWrapper>
      {showAutocomplete && (
        <>
          {shouldShowSuggestionForYou && (
            <StyledContainer>
              <Text type="label">{t('autoComplete.suggestionForYou')}</Text>
            </StyledContainer>
          )}
          <ScrollShadow>
            {searchSuggestions?.length === 0 && shouldShowSearchSuggestions ? (
              <StyledInfoBox>
                <Text align="center" type="body" size="small" color="black">
                  {t('autoComplete.pickupLocation.emptySlot.head', {
                    search: inputValue,
                  })}
                </Text>
              </StyledInfoBox>
            ) : (
              <StyledListbox
                listRef={autoCompleteRef}
                items={suggestions}
                getListItemProps={getListItemProps}
                getKey={getKeyFunction}
                cards
              >
                {(item: Suggestion) => (
                  <FullAddressListItem suggestion={item} searchTerm={inputValue} onItemClick={addressListItemOnClick} />
                )}
              </StyledListbox>
            )}
          </ScrollShadow>
        </>
      )}

      {isLoading && <StyledSpinner size={32} color="black" />}
    </>
  );
};

export default FullAddressSearch;
